// ✅ src/pages/VehicleV2/BillAddVehicle.jsx  (UPDATED FULL FILE)
// Supports:
// - selecting rows
// - passing BOTH progressId + progressDetId
// - saving selection in localStorage for BillGeneration tab / page
//
// Pattern updated to match your BillGenerationListing style:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + gridLoading states
// - cleaner init / search / bill flow
// - consistent card / section / button style

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaFileInvoiceDollar,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/billAddVehicle";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const parseDDMMYYYY = (s) => {
  if (!isDDMMYYYY(s)) return null;
  const [dd, mm, yyyy] = s.split("/").map((x) => Number(x));
  const dt = new Date(yyyy, mm - 1, dd);
  if (
    dt.getFullYear() !== yyyy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  ) {
    return null;
  }
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const isFutureDate = (s) => {
  const dt = parseDDMMYYYY(s);
  if (!dt) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return dt.getTime() > now.getTime();
};

const defaultFilters = {
  fromDate: "",
  toDate: "",
};

const BillAddVehicle = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehId =
    toInt(params.get("VehID")) ||
    toInt(params.get("Vehid")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;

  const vehType = (
    params.get("Type") ||
    params.get("type") ||
    params.get("brtype") ||
    "O"
  ).toUpperCase();

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehId}`, [vehId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  const [header, setHeader] = useState({
    vehicleNumber: "",
    period: "",
    toDate: "",
    basis: "",
    vehicleType: "",
  });

  const [filters, setFilters] = useState(defaultFilters);
  const [list, setList] = useState([]);

  // selected: { [progressId]: progressDetId }
  const [selected, setSelected] = useState({});

  const isBusy = pageLoading || gridLoading || billingLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const valueBoxClass = useMemo(
    () =>
      `px-3 py-2 rounded-lg border ${
        isDark
          ? "bg-gray-700/20 border-gray-600 text-gray-200"
          : "bg-gray-50 border-gray-200 text-gray-800"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const validateDates = useCallback(() => {
    const { fromDate, toDate } = filters;

    if (fromDate && !isDDMMYYYY(fromDate)) {
      return canUse(false, "From date must be dd/MM/yyyy");
    }
    if (toDate && !isDDMMYYYY(toDate)) {
      return canUse(false, "To date must be dd/MM/yyyy");
    }

    if (fromDate && parseDDMMYYYY(fromDate) == null) {
      return canUse(false, "Invalid From date");
    }
    if (toDate && parseDDMMYYYY(toDate) == null) {
      return canUse(false, "Invalid To date");
    }

    if (fromDate && isFutureDate(fromDate)) {
      return canUse(false, "Select from date lesser than current date");
    }
    if (toDate && isFutureDate(toDate)) {
      return canUse(false, "Select to date lesser than current date");
    }

    if (fromDate && toDate) {
      const f = parseDDMMYYYY(fromDate);
      const t = parseDDMMYYYY(toDate);
      if (t.getTime() < f.getTime()) {
        return canUse(false, "Please select from date less than to date");
      }
    }

    return true;
  }, [filters, canUse]);

  const resetFilters = useCallback((fromDate = "", toDate = "") => {
    setFilters({
      fromDate: fromDate || todayDDMMYYYY(),
      toDate: toDate || todayDDMMYYYY(),
    });
  }, []);

  const getProgressId = useCallback(
    (r, idx) => toInt(r?.fld_vehic_progs_id) || toInt(r?.id) || idx + 1,
    []
  );

  const getProgressDetId = useCallback(
    (r) =>
      toInt(
        r?.fld_vehic_progs_detid ??
          r?.fld_vehic_progsdetailid ??
          r?.fld_progress_detid ??
          r?.progressDetId ??
          r?.detId ??
          0,
        0
      ),
    []
  );

  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);

  const recordsCount = useMemo(() => list.length, [list]);

  const columns = useMemo(
    () => [
      { key: "date", label: "Date", w: "120px" },
      { key: "startingHrs", label: "Starting Hrs", w: "110px" },
      { key: "closingHrs", label: "Closing Hrs", w: "110px" },
      { key: "breakingHrs", label: "Breaking Hrs", w: "110px" },
      { key: "workingHrs", label: "Working Hrs", w: "110px" },
      { key: "cumulativeHrs", label: "Cumulative Hrs", w: "120px" },

      { key: "hourRent", label: "Hour Rent", w: "100px" },
      { key: "hourAmount", label: "Hour Amount", w: "110px" },
      {
        key: "hourCumulativeAmount",
        label: "Hour Cumulative Amount",
        w: "160px",
      },
      {
        key: "hourAdvanceAmount",
        label: "Hour Advance Amount",
        w: "150px",
      },

      { key: "dieselQty", label: "Diesel Qty", w: "100px" },

      { key: "startingKm", label: "Starting Km", w: "110px" },
      { key: "closingKm", label: "Closing Km", w: "110px" },
      { key: "workingKm", label: "Working Km", w: "110px" },
      { key: "cumulativeKm", label: "Cumulative Km", w: "120px" },

      { key: "rent", label: "Rent", w: "90px" },
      { key: "kmAmount", label: "Km Amount", w: "110px" },
      {
        key: "kmCumulativeAmount",
        label: "Km Cumulative Amount",
        w: "160px",
      },

      { key: "noOfLoads", label: "No.Of loads", w: "110px" },
    ],
    []
  );

  const renderCell = useCallback((row, key) => {
    const v = row?.[key];
    if (v === null || v === undefined || v === "") return "-";
    return String(v);
  }, []);

  const saveSelectionToLocalStorage = useCallback(
    (vehDetails, progressIds, progressDetIds) => {
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...existing,
            vehDetails: vehDetails ?? existing.vehDetails ?? null,
            progressIds: Array.isArray(progressIds)
              ? progressIds.map((x) => Number(x) || 0)
              : [],
            progressDetIds: Array.isArray(progressDetIds)
              ? progressDetIds.map((x) => Number(x) || 0)
              : [],
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }
    },
    [STORAGE_KEY, filters]
  );

  const goToBillGenerationWithData = useCallback(
    (vehDetails, progressIds, progressDetIds) => {
      const payload = {
        vehId,
        vehType,
        vehDetails,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      sessionStorage.setItem(`billProgress:${vehId}`, JSON.stringify(payload));

      saveSelectionToLocalStorage(vehDetails, progressIds, progressDetIds);

      navigate(
        `/vehicle/BillGeneration?VehID=${encodeURIComponent(
          vehId
        )}&Type=${encodeURIComponent(vehType)}`,
        {
          state: payload,
        }
      );
    },
    [vehId, vehType, filters, navigate, saveSelectionToLocalStorage]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehId, "VehID missing in URL. Use ?VehID=79&Type=O")) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          VehID: vehId,
          Type: vehType,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeader({
        vehicleNumber:
          data?.header?.vehicleNumber || data?.details?.regNo || "",
        period: data?.header?.period || "",
        toDate: data?.header?.toDate || "",
        basis: data?.details?.basis || "",
        vehicleType: data?.details?.veh_type || "",
      });

      resetFilters(
        data?.header?.fromDate || todayDDMMYYYY(),
        data?.header?.toDateInput || todayDDMMYYYY()
      );

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const sel = JSON.parse(raw);
          const pids = Array.isArray(sel?.progressIds) ? sel.progressIds : [];
          const pdet = Array.isArray(sel?.progressDetIds)
            ? sel.progressDetIds
            : [];

          if (pids.length) {
            const map = {};
            for (let i = 0; i < pids.length; i++) {
              const pid = toInt(pids[i], 0);
              map[pid] = toInt(pdet[i], 0) || 0;
            }
            setSelected(map);
          } else {
            setSelected({});
          }
        } else {
          setSelected({});
        }
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    vehId,
    vehType,
    commonHeaders,
    canUse,
    resetFilters,
    STORAGE_KEY,
  ]);

  const loadList = useCallback(async () => {
    if (!validateDates()) return;
    if (!canUse(vehId, "VehID missing in URL")) return;

    try {
      setGridLoading(true);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          VehID: vehId,
          Type: vehType,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        setList([]);
        toast.warn(data?.message || "No data found");
        return;
      }

      setHeader((p) => ({
        ...p,
        vehicleNumber: data?.header?.vehicleNumber || p.vehicleNumber,
        period: data?.header?.period || "",
        toDate: data?.header?.toDate || "",
        basis: data?.header?.basis || p.basis,
        vehicleType: data?.header?.vehicleType || p.vehicleType,
      }));

      const rows = data?.list || [];
      setList(rows);

      if (!rows.length) {
        toast.info("No Records Found!");
      } else {
        toast.success(`Found ${rows.length} record(s)`);
      }
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load list"
      );
    } finally {
      setGridLoading(false);
    }
  }, [
    validateDates,
    canUse,
    vehId,
    vehType,
    filters,
    branchId,
    loginUserId,
    commonHeaders,
  ]);

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const toggleRow = useCallback(
    (row, idx) => {
      const pid = getProgressId(row, idx);
      const detId = getProgressDetId(row) || 0;

      setSelected((p) => {
        const next = { ...p };
        if (next[pid] !== undefined) delete next[pid];
        else next[pid] = detId;
        return next;
      });
    },
    [getProgressId, getProgressDetId]
  );

  const handleRefresh = useCallback(async () => {
    setList([]);
    await loadInit();
  }, [loadInit]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onBill = useCallback(async () => {
    const progressIds = Object.keys(selected)
      .map((x) => toInt(x))
      .filter((x) => x > 0);

    if (!canUse(progressIds.length > 0, "Select at least one row")) return;

    const progressDetIds = progressIds.map(
      (pid) => toInt(selected[pid], 0) || 0
    );

    try {
      setBillingLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/add`,
        {
          VehID: vehId,
          Type: vehType,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          selectedIds: progressIds,
          selectedDetIds: progressDetIds,
          branchId,
          loginUserId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Add failed");
      }

      toast.success(data?.message || "Bill details calculated");

      goToBillGenerationWithData(
        data.vehDetails,
        progressIds,
        progressDetIds
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Bill error");
    } finally {
      setBillingLoading(false);
    }
  }, [
    selected,
    canUse,
    vehId,
    vehType,
    filters,
    branchId,
    loginUserId,
    commonHeaders,
    goToBillGenerationWithData,
  ]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BILL GENERATION - ADD VEHICLE
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Bill Generation - Add Vehicle
              </div>

              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"} {" • "} Vehicle ID: {vehId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Selected: {checkedCount}</span>
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {vehType === "O" ? "Own" : "Hire"}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <FaArrowLeft />
                  Back
                </span>
              </button>
            </div>
          </div>

          {/* Filters + Header Details */}
          <div className={sectionClass}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>From Date</label>
                <input
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={onFilterChange}
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  disabled={isBusy}
                />
              </div>

              <div>
                <label className={labelClass}>To Date</label>
                <input
                  name="toDate"
                  value={filters.toDate}
                  onChange={onFilterChange}
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  disabled={isBusy}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div>
                <label className={labelClass}>Vehicle Number</label>
                <div className={valueBoxClass}>
                  {header.vehicleNumber || "-"}
                </div>
              </div>

              <div>
                <label className={labelClass}>Period</label>
                <div className={valueBoxClass}>{header.period || "-"}</div>
              </div>

              <div>
                <label className={labelClass}>To Date</label>
                <div className={valueBoxClass}>{header.toDate || "-"}</div>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={loadList}
                disabled={isBusy}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
              >
                <FaSearch />
                {gridLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-800"
                }`}
              >
                Progress List
              </div>

              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Basis: <span className="font-semibold">{header.basis || "-"}</span>
                {" • "}
                Vehicle Type:{" "}
                <span className="font-semibold">
                  {header.vehicleType || "-"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{list.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-[1600px] w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">Check</th>
                    <th className="px-4 py-3 text-center">S.No</th>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className="px-4 py-3 text-center whitespace-nowrap"
                        style={{ width: c.w }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={
                    isDark
                      ? "bg-gray-900 text-gray-100"
                      : "bg-white text-gray-800"
                  }
                >
                  {list.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2 + columns.length}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        {gridLoading || pageLoading
                          ? "Loading..."
                          : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    list.map((row, idx) => {
                      const pid = getProgressId(row, idx);
                      const checked = selected[pid] !== undefined;
                      const detId = checked
                        ? selected[pid]
                        : getProgressDetId(row);

                      return (
                        <tr
                          key={pid}
                          title={`progressId=${pid} | progressDetId=${toInt(
                            detId,
                            0
                          )}`}
                          className={
                            isDark
                              ? "border-t border-gray-800 hover:bg-gray-800/60"
                              : "border-t border-gray-100 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(row, idx)}
                              disabled={isBusy}
                            />
                          </td>

                          <td className="px-4 py-3 text-center">{idx + 1}</td>

                          {columns.map((c) => (
                            <td
                              key={c.key}
                              className="px-4 py-3 text-center whitespace-nowrap"
                            >
                              {renderCell(row, c.key)}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={onBill}
                        disabled={isBusy || checkedCount === 0}
                        className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow disabled:opacity-60`}
                      >
                        <FaFileInvoiceDollar />
                        {billingLoading ? "Processing..." : "Bill"}
                      </button>
                    </td>
                    <td colSpan={1 + columns.length}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {isBusy && (
              <div
                className={`mt-3 text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {pageLoading
                  ? "Loading page..."
                  : gridLoading
                  ? "Loading grid..."
                  : billingLoading
                  ? "Preparing bill..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddVehicle;